import { z } from 'zod';
import { agentTranscriptSchema, pageSnapshotSchema } from './agent-context';
import { suggestOutput } from './agent-panel';

const itineraryAgentContext = z.object({
  contextType: z.literal('itinerary'),
  itineraryId: z.uuid(),
  dayId: z.uuid().nullish(),
});

// Operations context. The `organizationId` is informational and validated only
// to keep the discriminator strict — the API never trusts it: command handlers
// derive the org from `ctx.user.organizationId`. Including it here prevents
// the panel from accidentally submitting an empty / placeholder value.
const operationsAgentContext = z.object({
  contextType: z.literal('operations'),
  organizationId: z.uuid(),
});

// Products list page context. No additional fields beyond the discriminator
// — products are org-scoped via `ctx.user.organizationId` like the
// operations context, and the page itself is read-only catalog browsing
// (filters, search). When find_product runs under this context, the
// executor returns a `kind: 'apply_filters'` page-action instead of an
// in-panel ranked-suggestions list. See
// `.claude/rules/agent-panel-page-actions.md`.
const productsAgentContext = z.object({
  contextType: z.literal('products'),
});

// Itineraries list page context (plural — distinct from `itinerary` which
// is the per-itinerary detail page). Same shape as products: org-scoped
// via `ctx.user.organizationId`, no extra fields needed. When
// query_entity({ entity: 'itineraries' }) runs under this context the
// executor returns a `kind: 'apply_filters'` page-action so the
// natural-language filters flow into the page URL. See
// `.claude/rules/agent-panel-page-actions.md`.
const itinerariesAgentContext = z.object({
  contextType: z.literal('itineraries'),
});

export const conversationTurnSchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string().max(4_000),
});

export const commandSessionSnapshotSchema = z.object({
  command: z.string().nullable(),
  collected: z.record(z.string(), z.unknown()),
  history: z.array(conversationTurnSchema).max(20),
});

export const commandStepInput = z.discriminatedUnion('contextType', [
  itineraryAgentContext.extend({
    utterance: z.string().min(1).max(1_000),
    snapshot: commandSessionSnapshotSchema,
  }),
  operationsAgentContext.extend({
    utterance: z.string().min(1).max(1_000),
    snapshot: commandSessionSnapshotSchema,
  }),
  productsAgentContext.extend({
    utterance: z.string().min(1).max(1_000),
    snapshot: commandSessionSnapshotSchema,
  }),
  itinerariesAgentContext.extend({
    utterance: z.string().min(1).max(1_000),
    snapshot: commandSessionSnapshotSchema,
  }),
]);

export const commandResultSchema = z.object({
  kind: z.string(),
  data: z.record(z.string(), z.unknown()),
  humanSummary: z.string(),
});

// ---------------------------------------------------------------------------
// Per-kind result-data schemas. Renderers parse `CommandResult.data` against
// these via `parseResultData(result, schema)` so the framework stays
// `Record<string, unknown>` at the wire boundary while consumers get type
// safety. Add a new entry here when introducing a new `kind`.
// ---------------------------------------------------------------------------

export const productListResultData = z.object({
  suggest: suggestOutput,
  query: z.string().optional().nullable(),
  limit: z.number().optional(),
  refinements: z.record(z.string(), z.unknown()).optional(),
});

// Result data for `kind: 'apply_filters'` — the generic page-action
// envelope. `page` identifies which list page the filters are for;
// `filters` is the page's URL-search-params shape (validated by that
// page's `validateXxxSearch`); `summary` is the human-readable confirmation
// shown by the panel adapter's chip renderer.
//
// See `.claude/rules/agent-panel-page-actions.md` for the contract.
export const applyFiltersResultData = z.object({
  page: z.enum(['products', 'operations', 'itineraries']),
  filters: z.record(z.string(), z.unknown()),
  summary: z.string(),
  // How the page should combine `filters` with whatever's already in the
  // URL when this result lands. `replace` swaps array fields wholesale
  // (e.g. country: ['Spain'] replaces country: ['Portugal']); `union`
  // merges them so previous values are preserved (country: ['Portugal',
  // 'Spain']). The LLM picks based on phrasing — "find Spain hotels"
  // → replace, "also Spain" / "and Spain" → union. Default `replace`
  // keeps the legacy behaviour for older callers / tests.
  mergeMode: z.enum(['replace', 'union']).optional(),
  // The filter DSL string the agent applied (epic #273), surfaced verbatim
  // so the page can render it in an editable filter chip. Display-only — the
  // structured `filters` above drive the actual search.
  dsl: z.string().optional(),
  // Optional pre-resolved labels the page can use to render UI chips for
  // ID-only URL params (e.g. itinerary UUIDs the consultant hasn't picked
  // before). The adapter warms a label cache from these hints before
  // navigating so the toolbar's `MultiSelect` chips don't show raw UUIDs.
  // Cache misses fall back to UUIDs — labelHints is a perf hint, not
  // load-bearing.
  labelHints: z
    .object({
      itineraries: z
        .array(
          z.object({
            id: z.string(),
            title: z.string().nullable(),
            ref: z.string().nullable(),
          })
        )
        .optional(),
    })
    .optional(),
});

export type ProductListResultData = z.infer<typeof productListResultData>;
export type ApplyFiltersResultData = z.infer<typeof applyFiltersResultData>;

export const commandStepOutput = z.discriminatedUnion('phase', [
  z.object({
    phase: z.literal('collecting'),
    command: z.string(),
    collected: z.record(z.string(), z.unknown()),
    missing: z.array(z.string()),
    nextQuestion: z.string(),
    // Optional kind of custom input widget to render for the next reply,
    // looked up via the frontend's `getInputWidget(kind)`. Undefined means
    // render the default text input.
    inputWidget: z.string().optional(),
  }),
  z.object({
    phase: z.literal('confirm'),
    command: z.string(),
    collected: z.record(z.string(), z.unknown()),
    summary: z.string(),
  }),
  z.object({
    phase: z.literal('done'),
    command: z.string(),
    result: commandResultSchema,
  }),
  z.object({ phase: z.literal('cancelled') }),
  // Returned by `commandStep` when the planner picked a workflow. The
  // frontend should respond by calling `workflowStart` with the named
  // workflow (and the optional seed forwarded as-is).
  z.object({
    phase: z.literal('workflow-requested'),
    workflow: z.string(),
    seed: z.record(z.string(), z.unknown()).optional(),
  }),
  // Returned by `commandStep` when the planner picked the
  // `request_navigation` meta-tool: the consultant's intent matches an
  // action that lives on a different page. The frontend renders a confirm
  // card with `reason`; on confirm it navigates to the page that maps to
  // `target` and replays `replayUtterance` there to continue the
  // conversation against the new tool catalog. See the agent-panel
  // per-page-scoping PRD.
  z.object({
    phase: z.literal('navigate'),
    target: z.enum(['operations', 'products', 'itinerary', 'itineraries']),
    reason: z.string().min(1),
    replayUtterance: z.string().min(1),
  }),
]);

// ---------------------------------------------------------------------------
// Workflow procedures — `workflowStart` opens step 0; `workflowAdvance`
// transitions between steps. Both are stateless: the frontend ferries
// `completedResults` and `skippedStepIndices` between calls so the runner
// stays pure.
// ---------------------------------------------------------------------------

const workflowStepDescriptor = z.object({
  command: z.string(),
  label: z.string(),
  skippable: z.boolean(),
  loopable: z.boolean(),
});

const workflowStepOpenSchema = z.object({
  phase: z.literal('workflow-step'),
  workflow: z.string(),
  steps: z.array(workflowStepDescriptor),
  activeStepIndex: z.number().int().nonnegative(),
  command: z.string(),
  seedCollected: z.record(z.string(), z.unknown()),
  // Required keys still missing on this step, ordered by priority desc.
  // Empty when `seedCollected` already covers everything.
  missing: z.array(z.string()),
  // Pre-computed first question. Empty string when nothing is missing.
  nextQuestion: z.string(),
  // Set when missing=[] AND the command needs confirmation: the runner
  // pre-computed the confirm summary so the frontend can render the
  // confirm panel directly without a wasted round-trip.
  confirmSummary: z.string().optional(),
});

const workflowDoneSchema = z.object({
  phase: z.literal('workflow-done'),
  workflow: z.string(),
  steps: z.array(workflowStepDescriptor),
  completedResults: z.array(commandResultSchema),
});

const workflowCancelledSchema = z.object({
  phase: z.literal('workflow-cancelled'),
  workflow: z.string(),
});

export const workflowStartInput = z.discriminatedUnion('contextType', [
  itineraryAgentContext.extend({
    workflowName: z.string().min(1),
    // Pre-extracted fields the planner pulled from the consultant's
    // initial utterance — merged into step 0's `collected` BEFORE
    // `mapInputs` runs, so the consultant doesn't get re-asked for
    // things they already said. The runner validates these against the
    // first step's schema and silently drops any that don't fit.
    seed: z.record(z.string(), z.unknown()).optional(),
  }),
]);

export const workflowStartOutput = workflowStepOpenSchema;

export const workflowAdvanceAction = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('step-result'), result: commandResultSchema }),
  z.object({ kind: z.literal('skip') }),
  z.object({ kind: z.literal('cancel') }),
  z.object({ kind: z.literal('restart-step') }),
]);

export const workflowAdvanceInput = z.discriminatedUnion('contextType', [
  itineraryAgentContext.extend({
    workflowName: z.string().min(1),
    activeStepIndex: z.number().int().nonnegative(),
    completedResults: z.array(commandResultSchema),
    skippedStepIndices: z.array(z.number().int().nonnegative()),
    action: workflowAdvanceAction,
  }),
]);

export const workflowAdvanceOutput = z.discriminatedUnion('phase', [
  workflowStepOpenSchema,
  workflowDoneSchema,
  workflowCancelledSchema,
]);

export const commandExecuteInput = z.discriminatedUnion('contextType', [
  itineraryAgentContext.extend({
    command: z.string().min(1),
    params: z.record(z.string(), z.unknown()),
  }),
  operationsAgentContext.extend({
    command: z.string().min(1),
    params: z.record(z.string(), z.unknown()),
  }),
  productsAgentContext.extend({
    command: z.string().min(1),
    params: z.record(z.string(), z.unknown()),
  }),
  itinerariesAgentContext.extend({
    command: z.string().min(1),
    params: z.record(z.string(), z.unknown()),
  }),
]);

export const commandExecuteOutput = z.object({
  phase: z.literal('done'),
  command: z.string(),
  result: commandResultSchema,
});

// ---------------------------------------------------------------------------
// Agent Loop Engine (#276). `agentRun` is the multi-step loop entry point: a
// single natural-language `goal` the runner chains tools to satisfy. Shares
// the same per-page context discriminator as the command procedures so the
// runner can scope the ontology slice + reachable entities. v1 returns the
// whole step log in one response (streaming — US-5 — is a follow-up).
// ---------------------------------------------------------------------------

// `snapshot` carries the live page context (#274) — applied filters, visible
// columns, selection, route, permissions, and per-page `extras`. Optional so
// older callers / tests still validate; the runner degrades gracefully when
// it's absent. See `agent-context.ts`.
const agentRunFields = {
  goal: z.string().min(1).max(1_000),
  snapshot: pageSnapshotSchema.optional(),
  // Client-minted id, stable across a page session (P2.6 / P2.5). Keys the
  // server-side cross-turn undo stack and conversation-memory buffer. Optional
  // so older callers / tests still validate; the runner degrades without it.
  // An opaque string (not constrained to a UUID — the client falls back to a
  // non-UUID id when `crypto.randomUUID` is unavailable).
  sessionId: z.string().min(1).max(128).optional(),
  // Bounded transcript of recent turns the client replays each call so the
  // agent remembers context. Client-passed, capped + sanitised server-side —
  // descriptive only, never an authorisation input. See `agent-context.ts`.
  transcript: agentTranscriptSchema.optional(),
  // Resume a budget-stopped run (P4.1): the token from the prior final
  // event's `continuationToken`. Opaque, one-shot, server-minted and
  // user/context-scoped server-side — never an authorisation input.
  continuationToken: z.string().min(1).max(128).optional(),
};

export const agentRunInput = z.discriminatedUnion('contextType', [
  itineraryAgentContext.extend(agentRunFields),
  operationsAgentContext.extend(agentRunFields),
  productsAgentContext.extend(agentRunFields),
  itinerariesAgentContext.extend(agentRunFields),
]);

export const agentStepEventSchema = z.object({
  kind: z.literal('step'),
  label: z.string(),
  // 'running' is an in-flight progress beat emitted the moment a tool starts
  // executing — it carries the descriptive label of what the agent is doing
  // right now (e.g. "Searching products…"). The client shows it on the active
  // spinner row and does NOT append it to the completed-steps list; the
  // matching 'success'/'error' event lands when the tool finishes.
  status: z.enum(['success', 'error', 'running']),
  // Why an errored step failed (the tool envelope's `error`). A per-step error
  // is always mid-chain and recoverable — the loop retries — so the client
  // renders it muted with this as the tooltip/detail, not as a hard failure.
  detail: z.string().optional(),
  // The tool's CommandResult when it returned one. The client applies
  // page-action results (e.g. apply_filters) to the page via the adapter.
  result: commandResultSchema.optional(),
});

export const agentRunOutput = z.object({
  finalText: z.string(),
  steps: z.array(agentStepEventSchema),
  budgetExhausted: z.boolean(),
  // Undo token for the chain's reversible writes (US-6, #302), or null.
  undoToken: z.string().nullable(),
  // Server-minted id for this run, echoed for conversation memory + cross-turn
  // undo targeting (P2.5 / P2.6). Optional until the runner stamps it.
  runId: z.string().optional(),
  // True when the loop stopped on the step budget with work still to do; the
  // panel offers "Keep going" which re-invokes with `continuationToken` (P4.1).
  canContinue: z.boolean().optional(),
  continuationToken: z.string().nullable().optional(),
});

// `agentUndo` reverses a write chain by its token (US-6). One-shot + scoped to
// the calling user server-side.
export const agentUndoInput = z.object({ token: z.string().min(1) });
export const agentUndoOutput = z.object({ undone: z.number().int() });

// `agentUndoLast` (P2.6) reverses the most recent reversible chain for the
// caller's page session, without the client passing a token — powers the
// persistent "Undo last change" panel control. `token` is the chain it
// consumed, or null when there was nothing to undo (not an error).
export const agentUndoLastInput = z.object({
  sessionId: z.string().min(1).max(128),
});
export const agentUndoLastOutput = z.object({
  undone: z.number().int(),
  token: z.string().nullable(),
});

export type AgentUndoLastInput = z.infer<typeof agentUndoLastInput>;
export type AgentUndoLastOutput = z.infer<typeof agentUndoLastOutput>;

// `agentRunStream` (US-5, #301) yields these one at a time: live `step` events
// as the loop iterates, terminated by a single `final`. Mirrors the runner's
// `AgentStreamEvent` union in apps/agent-panel/api/src/loop/run-agent.ts.
export const agentFinalEventSchema = z.object({
  kind: z.literal('final'),
  finalText: z.string(),
  budgetExhausted: z.boolean(),
  undoToken: z.string().nullable(),
  // Server-minted id for this run, echoed so the client can record it in the
  // conversation transcript (P2.5) and target cross-turn undo (P2.6). Optional
  // until the runner stamps it.
  runId: z.string().optional(),
  // True when the loop stopped on the step budget with work still to do; the
  // panel offers "Keep going" which re-invokes with `continuationToken` (P4.1).
  canContinue: z.boolean().optional(),
  continuationToken: z.string().nullable().optional(),
});

// Emitted when a `requiresConfirm` tool needs explicit approval before it runs
// (P5.1). The loop pauses; the client renders a confirm card and calls
// `agentConfirm` with `runId` + `callId`. `severity` drives the card styling.
// Itinerary-planner scope only covers destructive content edits and
// non-deterministic generation — external send/spend confirms live elsewhere.
export const agentConfirmEventSchema = z.object({
  kind: z.literal('confirm'),
  runId: z.string().min(1),
  callId: z.string().min(1),
  // Human label for the pending action — never the internal tool name.
  toolLabel: z.string(),
  summary: z.string(),
  // Structured params/diff for the card (e.g. which day, how many items lost).
  preview: z.record(z.string(), z.unknown()).optional(),
  severity: z.enum(['destructive', 'generate']),
});

// Emitted when a tool returns a list of entities to render as inline cards
// rather than a one-line summary (P2.7) — e.g. recommended products, the
// day-by-day plan.
export const agentEntityRowsEventSchema = z.object({
  kind: z.literal('entity_rows'),
  entityType: z.string(),
  entities: z
    .array(
      z.object({
        id: z.string(),
        title: z.string(),
        subtitle: z.string().optional(),
        meta: z.record(z.string(), z.unknown()).optional(),
      })
    )
    .max(50),
});

// Coarse progress beat for long multi-step builds (P4.4) — "Day 3 of 7" —
// so the panel shows build progress rather than a flat spinner.
export const agentBuildProgressEventSchema = z.object({
  kind: z.literal('build_progress'),
  completed: z.number().int().nonnegative(),
  total: z.number().int().positive(),
  phase: z.string().optional(),
});

export const agentStreamEventSchema = z.discriminatedUnion('kind', [
  agentStepEventSchema,
  agentFinalEventSchema,
  agentConfirmEventSchema,
  agentEntityRowsEventSchema,
  agentBuildProgressEventSchema,
]);

// `agentConfirm` (P5.1) resolves a pending confirm gate for an in-flight run.
export const agentConfirmInput = z.object({
  runId: z.string().min(1),
  callId: z.string().min(1),
  decision: z.enum(['approve', 'reject']),
});
export const agentConfirmOutput = z.object({ resolved: z.boolean() });

export type ConversationTurn = z.infer<typeof conversationTurnSchema>;
export type CommandSessionSnapshot = z.infer<
  typeof commandSessionSnapshotSchema
>;
export type CommandStepInput = z.infer<typeof commandStepInput>;
export type CommandStepOutput = z.infer<typeof commandStepOutput>;
export type CommandExecuteInput = z.infer<typeof commandExecuteInput>;
export type CommandExecuteOutput = z.infer<typeof commandExecuteOutput>;
export type AgentRunInput = z.infer<typeof agentRunInput>;
export type AgentRunOutput = z.infer<typeof agentRunOutput>;
export type AgentUndoInput = z.infer<typeof agentUndoInput>;
export type AgentUndoOutput = z.infer<typeof agentUndoOutput>;
export type AgentStepEvent = z.infer<typeof agentStepEventSchema>;
export type AgentFinalEvent = z.infer<typeof agentFinalEventSchema>;
export type AgentConfirmEvent = z.infer<typeof agentConfirmEventSchema>;
export type AgentEntityRowsEvent = z.infer<typeof agentEntityRowsEventSchema>;
export type AgentBuildProgressEvent = z.infer<
  typeof agentBuildProgressEventSchema
>;
export type AgentStreamEvent = z.infer<typeof agentStreamEventSchema>;
export type AgentConfirmInput = z.infer<typeof agentConfirmInput>;
export type AgentConfirmOutput = z.infer<typeof agentConfirmOutput>;
export type CommandResult = z.infer<typeof commandResultSchema>;
export type WorkflowStartInput = z.infer<typeof workflowStartInput>;
export type WorkflowStartOutput = z.infer<typeof workflowStartOutput>;
export type WorkflowAdvanceInput = z.infer<typeof workflowAdvanceInput>;
export type WorkflowAdvanceOutput = z.infer<typeof workflowAdvanceOutput>;
export type WorkflowAdvanceAction = z.infer<typeof workflowAdvanceAction>;
export type WorkflowStepDescriptor = z.infer<typeof workflowStepDescriptor>;
